<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import AIDisclaimer from '@/components/AIDisclaimer.vue'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import BrowseGoalCard from '@/goals/BrowseGoalCard.vue'
import PresentLevelsLoadingGoalCards from '@/presentlevels/no-ai/PresentLevelsLoadingGoalCards.vue'
import { usePresentLevelsWizardStore } from '@/presentlevels/no-ai/present_levels_wizard.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getGoalSubjectLabelFromValue, getPlAreaLabelFromValue } from '@/contentprofile/no_sel_composable.js'
import { browseSubjectForPlwSubject, hasGradeFilterForAlignedGoals } from '@/presentlevels/no-ai/goal_recommendations'
import type { SkillNamesBySubject } from '@/presentlevels/no-ai/goal_recommendations'
import { GOAL_RECOMMENDATION_EVENT_NAMES } from '@/presentlevels/no-ai/event_names'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { toolkitUrls } from '@/util/links'
import type { GoalHit } from '@/goals/goal_types'

const BROWSE_REFERRER = 'plw'

const wizardStore = usePresentLevelsWizardStore()
const {
  goalRecommendations,
  isLoadingRecommendations,
  areRecommendationsRetrieved,
  hasGoalRecommendationsFailed,
  gradeForBrowse,
  draftedSkills,
  draftedStrengths
} = storeToRefs(wizardStore)
const contentProfileStore = useContentProfileStore()
const analytics = getAnalyticsTracker()

function uniqueSkillsBySubject(): SkillNamesBySubject {
  const skillsBySubject: SkillNamesBySubject = {}
  for (const [subject, skills] of Object.entries(draftedSkills.value.strengths)) {
    skillsBySubject[subject] = [...new Set(skills)]
  }
  for (const [subject, skills] of Object.entries(draftedSkills.value.needs)) {
    skillsBySubject[subject] = [...new Set([...(skillsBySubject[subject] ?? []), ...skills])]
  }
  return skillsBySubject
}

const draftedSkillsBySubject = computed(uniqueSkillsBySubject)

function recommendedGoalsForSkill(subject: string, skill: string): GoalHit[] {
  const recommendation = goalRecommendations.value.find((candidate) => candidate.subject === subject && candidate.category === skill)
  return recommendation?.goals ?? []
}

function isDraftedStrength(subject: string, skill: string): boolean {
  return draftedStrengths.value[subject]?.includes(skill) ?? false
}

function hasNoRecommendedGoals(subject: string, skill: string): boolean {
  return recommendedGoalsForSkill(subject, skill).length === 0
}

function browseGradeFilters(subject: string): string[] {
  const isGradeFiltered = hasGradeFilterForAlignedGoals(subject) && subject !== 'Transition'
  if (!isGradeFiltered) {
    return []
  }
  return [gradeForBrowse.value]
}

function browseSubject(subject: string): string {
  return getGoalSubjectLabelFromValue(browseSubjectForPlwSubject(subject)) || subject
}

function browseGoalsUrl(query: Record<string, string | string[]>): string {
  const params = new URLSearchParams()
  for (const [name, value] of Object.entries(query)) {
    for (const eachValue of [value].flat()) {
      params.append(name, eachValue)
    }
  }
  return `${toolkitUrls.browse}?${params.toString()}`
}

function browseSubjectUrl(subject: string): string {
  return browseGoalsUrl({ subject: browseSubject(subject), grades: browseGradeFilters(subject), ref: BROWSE_REFERRER })
}

const browseAllGoalsUrl = browseGoalsUrl({ ref: BROWSE_REFERRER })

function standardSetLabel(subject: string): string {
  return contentProfileStore.standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(subject))
}

function standardSetForRefDisplay(subject: string): string {
  return contentProfileStore.dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(subject))
}

function sendBrowseEvent(subject: string) {
  analytics.sendEvent(GOAL_RECOMMENDATION_EVENT_NAMES.CLICKED_GOAL_SHORTCUT, {
    plw_goal_shortcut_subject: subject,
    plw_goal_shortcut_grades: browseGradeFilters(subject),
    source: 'aligned goals'
  })
}

function sendDisclaimerOpenedEvent() {
  analytics.sendEvent(GOAL_RECOMMENDATION_EVENT_NAMES.OPENED_AI_DISCLAIMER)
}
</script>

<template>
  <div>
    <AIDisclaimer
      v-if="!areRecommendationsRetrieved"
      class="mb-4"
      :is-beta="false"
      @opened="sendDisclaimerOpenedEvent"
    />
    <section v-if="!hasGoalRecommendationsFailed">
      <div
        v-for="(skills, subject) in draftedSkillsBySubject"
        :key="subject"
        class="mb-12"
      >
        <h2 class="mb-3 text-display-md font-medium">
          {{ getPlAreaLabelFromValue(subject) }}
          <span v-if="!isLoadingRecommendations" class="text-text-2xl font-regular">
            - Browse more content in
            <a
              :href="browseSubjectUrl(subject)"
              target="_blank"
              class="text-text-xl font-regular text-blue-400 hover:underline"
              @click="sendBrowseEvent(subject)"
            >{{ getPlAreaLabelFromValue(subject) }} <OpensInNewTabIcon /></a>
          </span>
        </h2>
        <section
          v-for="(skill, index) in skills"
          :key="`${subject}-${skill}-${index}`"
          class="mb-6"
        >
          <template v-if="!isLoadingRecommendations">
            <h3 class="mb-2 text-text-2xl font-regular">{{ skill }}</h3>
            <p v-if="isDraftedStrength(subject, skill)" class="text-text-lg">
              This area was selected as a strength. For aligned goals, edit the statement for an area of need.
            </p>
            <p v-else-if="hasNoRecommendedGoals(subject, skill)" class="text-text-lg">
              No aligned goals found.
            </p>
            <ul v-else class="flex flex-col flex-wrap md:flex-row">
              <li
                v-for="(goal, position) in recommendedGoalsForSkill(subject, skill)"
                :key="goal.uuid"
                class="mb-6 px-1 md:w-1/4"
              >
                <BrowseGoalCard
                  :goal="goal"
                  :position="position"
                  :standard-set-label="standardSetLabel(subject)"
                  :standard-set-for-ref-display="standardSetForRefDisplay(subject)"
                  is-aligned-goals-tile
                />
              </li>
            </ul>
          </template>
          <PresentLevelsLoadingGoalCards v-else />
        </section>
      </div>
    </section>
    <div
      v-else
      class="my-4 max-w-[720px] rounded-xxs border border-blue-300 bg-blue-200 p-2 text-text-md text-black"
    >
      <span class="material-icons-round align-text-bottom text-text-lg text-blue-400" aria-hidden="true">info_outline</span>
      We're not able to retrieve aligned goals right now. Try again later. To view more content,
      <a
        :href="browseAllGoalsUrl"
        target="_blank"
        class="text-blue-400 hover:underline"
      >Browse Goals <OpensInNewTabIcon /></a>
    </div>
  </div>
</template>
